import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, KeyRound, Building2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ username: "", password: "", name: "", role: "tenant" });
  const [pwTarget, setPwTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [sites, setSites] = useState([]);
  const [siteTarget, setSiteTarget] = useState(null);
  const [checkedSites, setCheckedSites] = useState(new Set());

  const load = () => {
    api.get("/admin/users").then((res) => setUsers(res.data)).catch(() => {});
    api.get("/admin/sites").then((res) => setSites(res.data)).catch(() => {});
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/users", form);
      toast.success(`Account created for ${form.username}`);
      setOpen(false);
      setForm({ username: "", password: "", name: "", role: "tenant" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Account deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const resetPw = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${pwTarget.id}/password`, { password: newPassword });
      toast.success(`Password updated for ${pwTarget.username}`);
      setPwTarget(null);
      setNewPassword("");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openSiteAssign = (u) => { setSiteTarget(u); setCheckedSites(new Set(u.site_ids || [])); };

  const saveSites = async () => {
    try {
      await api.put(`/admin/users/${siteTarget.id}/sites`, { site_ids: [...checkedSites] });
      toast.success(`Site access updated for ${siteTarget.username}`);
      setSiteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-100">User Accounts</h3>
        <Button data-testid="create-user-btn" onClick={() => setOpen(true)} size="sm"
          className="bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
          <Plus className="w-4 h-4 mr-1.5" /> New Account
        </Button>
      </div>

      <div className="ops-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead>
              <tr className="text-left ops-caption border-b border-[#1E2638] bg-[#0A0D14]">
                <th className="px-4 py-3">Username</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3">Sites</th>
                <th className="px-4 py-3">Presence</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500" data-testid="users-empty">No tenant accounts yet.</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1E2638]/50 last:border-0 hover:bg-[#0A0D14]/60" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-mono text-emerald-400">@{u.username}</td>
                  <td className="px-4 py-3 text-slate-200">{u.name || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
                      u.role === "manager"
                        ? "text-cyan-400 bg-cyan-500/10 border-cyan-500/30"
                        : "text-slate-300 bg-slate-500/10 border-slate-500/30"}`}
                      data-testid={`user-role-${u.id}`}>
                      {(u.role || "tenant").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-400" data-testid={`user-sites-${u.id}`}>
                    {u.role === "manager" ? `${(u.site_ids || []).length} site(s)` : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-mono ${u.online ? "text-emerald-400" : "text-slate-500"}`}
                      data-testid={`user-presence-${u.id}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.online ? "bg-emerald-400 pulse-dot" : "bg-slate-600"}`} />
                      {u.online ? "ONLINE" : "OFFLINE"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-500 font-mono">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {u.role === "manager" && (
                      <Button data-testid={`assign-sites-${u.id}`} variant="ghost" size="icon" title="Assign sites"
                        onClick={() => openSiteAssign(u)} className="h-8 w-8 text-cyan-400 hover:text-cyan-300">
                        <Building2 className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button data-testid={`reset-password-${u.id}`} variant="ghost" size="icon" title="Reset password"
                      onClick={() => setPwTarget(u)} className="h-8 w-8 text-amber-400 hover:text-amber-300">
                      <KeyRound className="w-3.5 h-3.5" />
                    </Button>
                    <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" onClick={() => remove(u)}
                      className="h-8 w-8 text-slate-400 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="user-dialog">
          <DialogHeader><DialogTitle>New Tenant Account</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Username</Label>
              <Input data-testid="user-username-input" value={form.username} required
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638] font-mono" placeholder="unit4b" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Display Name</Label>
              <Input data-testid="user-name-input" value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638]" placeholder="John Smith — Unit 4B" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select" className="bg-[#0A0D14] border-[#1E2638]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0F131C] border-[#1E2638] text-slate-100">
                  <SelectItem value="tenant">Tenant — receives calls</SelectItem>
                  <SelectItem value="manager">Site Manager — views sites & shares credentials</SelectItem>
                </SelectContent>
              </Select>
              {form.role === "manager" && (
                <p className="text-xs text-slate-500">After creating, use the sites icon in the row to assign which sites they manage.</p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Password</Label>
              <Input data-testid="user-password-input" type="password" value={form.password} required minLength={6}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="bg-[#0A0D14] border-[#1E2638]" placeholder="Share this with the tenant" />
            </div>
            <Button type="submit" data-testid="user-submit-btn"
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
              Create Account
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!pwTarget} onOpenChange={() => setPwTarget(null)}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="reset-password-dialog">
          <DialogHeader><DialogTitle>Reset Password — @{pwTarget?.username}</DialogTitle></DialogHeader>
          <form onSubmit={resetPw} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-300">New Password</Label>
              <Input data-testid="reset-password-input" type="password" value={newPassword} required minLength={6}
                onChange={(e) => setNewPassword(e.target.value)}
                className="bg-[#0A0D14] border-[#1E2638]" />
            </div>
            <Button type="submit" data-testid="reset-password-submit-btn"
              className="w-full bg-amber-500 hover:bg-amber-600 text-black font-semibold">
              Update Password
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!siteTarget} onOpenChange={() => setSiteTarget(null)}>
        <DialogContent className="bg-[#0F131C] border-[#1E2638] text-slate-100" data-testid="site-assign-dialog">
          <DialogHeader><DialogTitle>Assign Sites — @{siteTarget?.username}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {sites.map((s) => (
              <label key={s.id} data-testid={`site-check-${s.id}`}
                className="flex items-center gap-3 bg-[#0A0D14] border border-[#1E2638] rounded-lg px-3 py-2.5 cursor-pointer hover:border-emerald-500/40">
                <input type="checkbox" checked={checkedSites.has(s.id)}
                  onChange={(e) => {
                    const next = new Set(checkedSites);
                    if (e.target.checked) next.add(s.id); else next.delete(s.id);
                    setCheckedSites(next);
                  }}
                  className="accent-emerald-500 w-4 h-4" />
                <span className="text-sm text-slate-200">{s.name}</span>
              </label>
            ))}
            {sites.length === 0 && <p className="text-sm text-slate-500">No sites yet — create one first.</p>}
          </div>
          <Button data-testid="site-assign-save-btn" onClick={saveSites}
            className="w-full bg-emerald-500 hover:bg-emerald-600 text-emerald-950 font-semibold">
            Save Site Access
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
